/**
 * Bundled catalog port → city / country / ISO2 / timezone map.
 * Mirrors iOS CruisePortCityMap (import_port_city_country_name_map.csv).
 */

import csvRaw from '../data/import_port_city_country_name_map.csv?raw';

export interface CruisePortCityMapRow {
  port: string;
  city: string;
  timeZoneIdentifier: string;
  country: string;
  countryISO2: string;
}

let cachedRows: Map<string, CruisePortCityMapRow> | null = null;

export function normalizedPortKey(port: string): string {
  return port.trim().toLowerCase();
}

/** Quote-aware CSV line split (handles `"Darwin, NT Australia",Darwin,...`). */
export function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let insideQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }
    if (char === ',' && !insideQuotes) {
      fields.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  fields.push(current);
  return fields;
}

export function parsePortCityMapCsv(csv: string): Map<string, CruisePortCityMapRow> {
  const result = new Map<string, CruisePortCityMapRow>();
  // Strip UTF-8 BOM if present
  const text = csv.replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/);
  if (lines.length <= 1) return result;

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line) continue;
    const fields = parseCsvLine(line);
    if (fields.length < 5) continue;
    const port = fields[0].trim();
    const city = fields[1].trim();
    const timeZoneIdentifier = fields[2].trim();
    const country = fields[3].trim();
    const countryISO2 = fields[4].trim().toUpperCase();
    if (!port || !city || !country || countryISO2.length !== 2) continue;
    const row: CruisePortCityMapRow = {
      port,
      city,
      timeZoneIdentifier,
      country,
      countryISO2,
    };
    result.set(normalizedPortKey(port), row);
  }
  return result;
}

function rowsByPort(): Map<string, CruisePortCityMapRow> {
  if (cachedRows) return cachedRows;
  cachedRows = parsePortCityMapCsv(csvRaw);
  return cachedRows;
}

/** Lookup by full API port string (e.g. `"Darwin, NT Australia"`), not truncated display name. */
export function rowForPort(apiPort: string): CruisePortCityMapRow | null {
  const key = normalizedPortKey(apiPort);
  if (!key) return null;
  return rowsByPort().get(key) ?? null;
}

/** Test hooks — swap CSV without reloading the module graph. */
export function resetPortCityMapCacheForTesting(): void {
  cachedRows = null;
}

export function loadPortCityMapCsvForTesting(csv: string): void {
  cachedRows = parsePortCityMapCsv(csv);
}
